import React, { lazy, Suspense, useState, useEffect, useRef } from 'react';
import { ModelPhotosProvider } from './components/ModelAvatar';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider, useColors } from './context/ThemeContext';
import { SocketProvider } from './context/SocketContext';
import RoleGuard from './components/RoleGuard';
import Sidebar from './components/Sidebar';
import BottomNav from './components/BottomNav';
import CommandPalette from './components/CommandPalette';
import WorkspaceBar from './components/WorkspaceBar';
import { PageBoundary, PageLoading } from './components/PageState';

// Login stays eager — it is the first paint for anyone without a session.
// Every other page is split out so a chatter on mobile does not download
// Finance's PDF stack or Reddit Operations before seeing their own screen.
import Login from './pages/Login';
import { defaultRouteForUser } from './lib/pageAccess';

const CommandCenter = lazy(() => import('./pages/CommandCenter'));
const Scheduler = lazy(() => import('./pages/Scheduler'));
const ReelsOperations = lazy(() => import('./pages/ReelsOperations'));
const ChatterCRM = lazy(() => import('./pages/ChatterCRM'));
const SalesOverview = lazy(() => import('./pages/SalesOverview'));
const SundayReview = lazy(() => import('./pages/SundayReview'));
const Pipeline = lazy(() => import('./pages/Pipeline'));
const ExternalTool = lazy(() => import('./pages/ExternalTool'));
const RedditOperations = lazy(() => import('./pages/RedditOperations'));
const Documents = lazy(() => import('./pages/Documents'));
const TelegramAlerts = lazy(() => import('./pages/TelegramAlerts'));
const Settings = lazy(() => import('./pages/Settings'));
const OnboardingPage = lazy(() => import('./pages/Onboarding'));
const ModelForm = lazy(() => import('./pages/ModelForm'));
const Finance = lazy(() => import('./pages/Finance'));
const ModelsHub = lazy(() => import('./pages/ModelsHub'));
const Tasks = lazy(() => import('./pages/Tasks'));
const ContentOS = lazy(() => import('./pages/ContentOS'));
const CreatorOperations = lazy(() => import('./pages/CreatorOperations'));
const Knowledge = lazy(() => import('./pages/Knowledge'));
const KnowledgeShare = lazy(() => import('./pages/KnowledgeShare'));


function useIsMobile() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return isMobile;
}

function Layout({ children }) {
  const { user } = useAuth();
  const C = useColors();
  const isMobile = useIsMobile();
  const location = useLocation();
  const isLogin = location.pathname === '/login';
  const isPublic = location.pathname.startsWith('/m/') || location.pathname.startsWith('/knowledge/share/');
  const [sidebarWidth, setSidebarWidth] = useState(() => {
    try {
      const width = Number(localStorage.getItem('cuzo_sidebar_width'));
      return width === 64 ? 64 : width >= 200 && width <= 340 ? width : 224;
    } catch { return 224; }
  });
  const expandedSidebarWidth = useRef(sidebarWidth === 64 ? 224 : sidebarWidth);
  const resizeSidebar = width => {
    if (width > 64) expandedSidebarWidth.current = width;
    setSidebarWidth(width);
    try { localStorage.setItem('cuzo_sidebar_width', String(width)); } catch { /* Layout still works without storage. */ }
  };

  if (!user || isLogin || isPublic) return children;

  return (
    <div className="cuzo-app-shell" style={{ background: C.bg, '--sidebar-w': `${sidebarWidth}px` }}>
      <a href="#cuzo-main" className="cuzo-skip-link">Skip to workspace</a>
      {!isMobile && <Sidebar width={sidebarWidth} onResize={resizeSidebar} />}
      <main id="cuzo-main" tabIndex={-1} className={isMobile ? 'is-mobile' : 'is-desktop'} style={{
        marginLeft: isMobile ? 0 : undefined,
        paddingBottom: isMobile ? 'calc(76px + env(safe-area-inset-bottom))' : 0,
        background: C.bg,
        color: C.text1,
      }}>
        <WorkspaceBar compact={sidebarWidth === 64} isMobile={isMobile}
          onToggleFocus={() => resizeSidebar(sidebarWidth === 64 ? expandedSidebarWidth.current : 64)} />
        <div className="workspace-content" key={location.pathname}>
          <PageBoundary key={location.pathname}>{children}</PageBoundary>
        </div>
      </main>
      {isMobile && <BottomNav />}
    </div>
  );
}

export default function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <ModelPhotosProvider>
        <BrowserRouter>
          <SocketWrapper />
        </BrowserRouter>
        </ModelPhotosProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

function SocketWrapper() {
  return (
    <SocketProvider>
      <CommandPalette />
      <Layout>
        <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/command-center" element={<RoleGuard roles={['owner']} page="Command Center"><CommandCenter /></RoleGuard>} />
          <Route path="/scheduler" element={<RoleGuard roles={['owner','va']} page="Reels Operations"><Scheduler /></RoleGuard>} />
          <Route path="/reels-operations" element={<RoleGuard roles={['owner','va']} page="Reels Operations"><ReelsOperations /></RoleGuard>} />
          <Route path="/chatters" element={<RoleGuard roles={['owner','chatter']} page="Chatters CRM"><ChatterEntry /></RoleGuard>} />
          <Route path="/review" element={<RoleGuard roles={['owner']} page="Sunday Review"><SundayReview /></RoleGuard>} />
          <Route path="/pipeline" element={<RoleGuard roles={['owner']} page="Pipeline"><Pipeline /></RoleGuard>} />
          <Route path="/sales-overview" element={<RoleGuard roles={['owner']} page="Sales Overview"><SalesOverview /></RoleGuard>} />
          <Route path="/ai-content" element={<RoleGuard roles={['owner','va']} page="AI Generator"><ExternalTool title="AI Content Generator" url="https://content-factory-production-902b.up.railway.app" /></RoleGuard>} />
          {/* Reddit Operations is a real section now, not an iframe. The splat
              lets the shell own its own sub-routes. */}
          <Route path="/reddit-operations/*" element={<RoleGuard roles={['owner','va','chatter']} page="Reddit Operations"><RedditOperations /></RoleGuard>} />
          <Route path="/reddit-dashboard" element={<Navigate to="/reddit-operations" replace />} />
          <Route path="/documents" element={<RoleGuard roles={['owner','va','chatter']} page="Documents"><Documents /></RoleGuard>} />
          <Route path="/telegram" element={<RoleGuard roles={['owner','va']} page="Send Alert"><TelegramAlerts /></RoleGuard>} />
          <Route path="/users" element={<RoleGuard roles={['owner']}><Navigate to="/settings?section=team" replace /></RoleGuard>} />
          <Route path="/settings" element={<RoleGuard roles={['owner']}><Settings /></RoleGuard>} />
          <Route path="/onboarding" element={<RoleGuard roles={['owner','va']} page="Model Onboarding"><OnboardingPage /></RoleGuard>} />
          <Route path="/finance/*" element={<RoleGuard roles={['owner']} page="Finance"><Finance /></RoleGuard>} />
          <Route path="/models-hub" element={<RoleGuard roles={['owner','va']} page="Models Hub"><ModelsHub /></RoleGuard>} />
          <Route path="/tasks" element={<RoleGuard roles={['owner','va','chatter']} page="Tasks"><Tasks /></RoleGuard>} />
          <Route path="/content-os" element={<RoleGuard roles={['owner','va']} page="Content OS"><ContentOS /></RoleGuard>} />
          <Route path="/creator-operations" element={<RoleGuard roles={['owner','va']} page="Models Hub"><CreatorOperations /></RoleGuard>} />
          <Route path="/brain" element={<Navigate to="/knowledge" replace />} />
          <Route path="/knowledge" element={<RoleGuard roles={['owner','va','chatter']} page="Knowledge"><Knowledge /></RoleGuard>} />
          <Route path="/knowledge/share/:token" element={<KnowledgeShare />} />
          <Route path="/m/:token" element={<ModelForm />} />
          <Route path="*" element={<DefaultRedirect />} />
        </Routes>
        </Suspense>
      </Layout>
    </SocketProvider>
  );
}

// Keep navigation usable and provide recovery if a route download stalls.
function RouteFallback() {
  return <PageLoading />;
}

function DefaultRedirect() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={defaultRouteForUser(user)} replace />;
}

// Owners work with chatters from Sales Overview. The standalone route remains
// for the chatter role, which needs its focused working desk.
function ChatterEntry() {
  const { user } = useAuth();
  return user?.role === 'owner' ? <Navigate to="/sales-overview?view=chatters" replace /> : <ChatterCRM />;
}
